import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon, ChevronDownIcon, IdCardIcon, MapPinIcon, MenuIcon, SearchIcon, SmartphoneIcon, UserRoundIcon, XIcon } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import { BellPreview } from './avis/BellPreview';
import { HeaderTooltip } from './HeaderTooltip';
import { navItems, utilityLinks } from '../data/navigation';

/** Les modules bciti+ ajoutés à la barre de navigation de la Ville. */
const modules = [{
  label: 'Plan',
  to: '/plan',
  Icon: MapPinIcon
}, {
  label: 'Ma carte',
  to: '/ma-carte',
  Icon: IdCardIcon
}, {
  label: 'Mon espace',
  to: '/mon-espace',
  Icon: SmartphoneIcon
}, {
  label: 'Mon profil',
  to: '/mon-profil',
  Icon: UserRoundIcon
}];
const moduleButton = 'group relative grid h-10 w-10 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/15 hover:text-ville-lime focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-lime';
export function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  return <header className="w-full bg-white" data-id="element-1353" data-mp="2mmwy66h2nmdo">
      <div className="mx-auto flex max-w-[1500px] items-stretch" data-id="element-1354" data-mp="2ho3mmjlci5g5">
        <div className="flex shrink-0 items-center px-5 py-4 lg:px-8" data-id="element-1355" data-mp="2hpoeg1g9zaab">
          <Link to="/" className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-coral" data-id="element-1356" data-mp="3fasw1eidnkc1">
            <Logo data-id="element-1357" data-mp="3ad110bmfri3m" />
          </Link>
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-end" data-id="element-1358" data-mp="35kr6eiadltv0">
          <div className="hidden items-center justify-end gap-6 px-5 py-3 lg:flex lg:px-8" data-id="element-1359" data-mp="35j6ez1jcyr1d">
            <nav aria-label="Navigation utilitaire" data-id="element-1360" data-mp="30klldkym35lw">
              <ul className="flex items-center gap-6" data-id="element-1361" data-mp="1xhc1ossslud">
                {utilityLinks.map(item => item.to ? <li key={item.label} data-id="element-1362" data-mp="3t90mr4otlper">
                      <Link to={item.to} className="text-[15px] text-ville-navy transition-colors duration-150 ease-ville hover:text-ville-coral" data-id="element-1363" data-mp="1rd1lt0df4tlh">
                        {item.label}
                      </Link>
                    </li> : <li key={item.label} data-id="element-1364" data-mp="1mla2nawldq5d">
                      <a href="#accueil" className="text-[15px] text-ville-navy transition-colors duration-150 ease-ville hover:text-ville-coral" data-id="element-1365" data-mp="18n3dqk9mf0hu">
                        {item.label}
                      </a>
                    </li>)}
              </ul>
            </nav>

            <button type="button" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ville-mist py-1.5 pl-1.5 pr-4 text-[15px] font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-lime focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-coral" data-id="element-1366" data-mp="13q36i611brp5">
              <span className="grid h-8 w-8 place-items-center rounded-full bg-white text-ville-navy" aria-hidden="true" data-id="element-1367" data-mp="hgcw2ujt353q">
                <SearchIcon className="h-4 w-4" data-id="element-1368" data-mp="w59d3h3ubsgh" />
              </span>
              Rechercher
            </button>
          </div>

          <div className="bg-ville-navy" data-id="element-1369" data-mp="rbofhq65t0ht">
            <div className="flex items-center gap-1 px-5 lg:px-8" data-id="element-1370" data-mp="154cyirqhcjy5">
              <nav aria-label="Navigation principale" className="hidden lg:block" onMouseLeave={() => setOpenMenu(null)} data-id="element-1371" data-mp="106326dt0rday">
                <ul className="flex items-center" data-id="element-1372" data-mp="1f1952rwtiw02">
                  {navItems.map(item => {
                  const isOpen = openMenu === item.label;
                  if (item.children.length === 0) {
                    return <li key={item.label} data-id="element-1373" data-mp="3bwds3vt7ewl9">
                          <a href="#accueil" className="block px-4 py-5 text-[17px] font-medium text-white transition-colors duration-150 ease-ville hover:text-ville-lime focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-lime" data-id="element-1374" data-mp="3bxoupsawxyas">
                            {item.label}
                          </a>
                        </li>;
                  }
                  return <li key={item.label} className="relative" data-id="element-1375" data-mp="1k62yqmchrmix">
                        <button type="button" aria-expanded={isOpen} onMouseEnter={() => setOpenMenu(item.label)} onClick={() => setOpenMenu(isOpen ? null : item.label)} className={`flex items-center gap-1.5 px-4 py-5 text-[17px] font-medium transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-lime ${isOpen ? 'text-ville-lime' : 'text-white hover:text-ville-lime'}`} data-id="element-1376" data-mp="1p822hxc58lge">
                          {item.label}
                          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-ville ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1377" data-mp="oykh6tzp2yl7" />
                        </button>

                        {isOpen && <div className="absolute left-0 top-full z-50 w-80 pt-1" data-id="element-1378" data-mp="a94ubp3abdas">
                            <ul className="rounded-b-2xl bg-white p-2 shadow-xl shadow-ville-navy/25 ring-1 ring-ville-mist" data-id="element-1379" data-mp="5al4hdlklhd5">
                              {item.children.map(child => <li key={child} data-id="element-1380" data-mp="3sgc5nyemh0en">
                                  <a href="#accueil" className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-coral" data-id="element-1381" data-mp="3sf12o0t03wol">
                                    {child}
                                  </a>
                                </li>)}
                            </ul>
                          </div>}
                      </li>;
                })}
                </ul>
              </nav>

              <div className="ml-auto flex items-center gap-1 py-2.5" data-id="element-1382" data-mp="5c8vvubjtwue">
                <div className="hidden items-center gap-1 md:flex" data-id="element-1383" data-mp="3mu1tt2kt4eex">
                  <Link to={modules[0].to} aria-label="Plan" className={moduleButton} data-id="element-1384" data-mp="3mrpekx6foi5k">
                    <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-1385" data-mp="3rr22zmcydxa9" />
                    <HeaderTooltip label="Plan" data-id="element-1386" data-mp="jc0fqboqy7gb" />
                  </Link>
                  <Link to={modules[1].to} aria-label="Ma carte" className={moduleButton} data-id="element-1387" data-mp="o4bhzl6ym5c1">
                    <IdCardIcon className="h-5 w-5" aria-hidden="true" data-id="element-1388" data-mp="28txf8mld9tv1" />
                    <HeaderTooltip label="Ma carte" data-id="element-1389" data-mp="28s47f3z0p3lg" />
                  </Link>

                  <div className="relative" data-id="element-1390" data-mp="23ukovrvae4np">
                    <button type="button" aria-label="Mes avis, 3 non lus dont une alerte urgente" aria-expanded={bellOpen} onClick={() => setBellOpen(v => !v)} className={moduleButton} data-id="element-1391" data-mp="2ijqnpeq7psv2">
                      <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-1392" data-mp="1kx1cce9me3wj" />
                      <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ville-coral px-1 text-[11px] font-bold text-white ring-2 ring-ville-navy" aria-hidden="true" data-id="element-1393" data-mp="1yvrdc4zk8nq6">
                        3
                      </span>
                      {!bellOpen && <HeaderTooltip label="Mes avis" data-id="element-1394" data-mp="369b9e5dr3481" />}
                    </button>
                    <AnimatePresence data-id="element-1395" data-mp="13o8p5ts9d1jh">
                      {bellOpen && <BellPreview onNavigate={() => setBellOpen(false)} data-id="element-1396" data-mp="18lqy3k69zgrh" />}
                    </AnimatePresence>
                  </div>

                  <Link to={modules[2].to} aria-label="Mon espace" className={moduleButton} data-id="element-1397" data-mp="1dk24tm19xb5h">
                    <SmartphoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1398" data-mp="1icvaqd8hapj5" />
                    <HeaderTooltip label="Mon espace" data-id="element-1399" data-mp="1mdx0m3gi9p8g" />
                  </Link>
                  <Link to={modules[3].to} aria-label="Mon profil" className={moduleButton} data-id="element-1400" data-mp="1rci369j8cl02">
                    <UserRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-1401" data-mp="1reb89ke72x5y" />
                    <HeaderTooltip label="Mon profil" data-id="element-1402" data-mp="1wccrq6fj8yhy" />
                  </Link>
                </div>

                <button type="button" aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} className="grid h-10 w-10 place-items-center rounded-full text-white transition-colors duration-150 ease-ville hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-lime lg:hidden" data-id="element-1403" data-mp="219wg3z5y2rnm">
                  {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-1404" data-mp="2bcibffc2einv" /> : <MenuIcon className="h-5 w-5" data-id="element-1405" data-mp="26frvqt469p2z" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {mobileOpen && <nav aria-label="Navigation mobile" className="bg-ville-deep px-5 pb-6 lg:hidden" data-id="element-1406" data-mp="333ocpz3iumeq">
          <ul className="grid gap-2 py-4 md:hidden" data-id="element-1407" data-mp="2z22urzc4rhxo">
            {modules.map(module => <li key={module.label} data-id="element-1408" data-mp="2u39jmdkbg30o">
                <Link to={module.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 text-[15px] font-bold text-white" data-id="element-1409" data-mp="2p69jeja5duhh">
                  <module.Icon className="h-5 w-5" aria-hidden="true" data-id="element-1410" data-mp="1dp9mtbgrothn" />
                  {module.label}
                </Link>
              </li>)}
          </ul>
          <ul className="divide-y divide-white/10" data-id="element-1411" data-mp="184vy0xx2sh1h">
            {navItems.map(item => <li key={item.label} className="py-3" data-id="element-1412" data-mp="1msz221rk5inn">
                <p className="text-sm font-extrabold uppercase tracking-wide text-ville-lime" data-id="element-1413" data-mp="1mujzq0965hpq">{item.label}</p>
                {item.children.length > 0 && <ul className="mt-2 space-y-1" data-id="element-1414" data-mp="1vs40z9717e0c">
                    {item.children.map(child => <li key={child} data-id="element-1415" data-mp="1qtj3hhj5z8fl">
                        <a href="#accueil" className="block py-1.5 text-[15px] text-white/80" data-id="element-1416" data-mp="1qwosyxvpgcbu">
                          {child}
                        </a>
                      </li>)}
                  </ul>}
              </li>)}
          </ul>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-4" data-id="element-1417" data-mp="bfeuoplkf72y">
            {utilityLinks.map(item => <li key={item.label} data-id="element-1418" data-mp="2i1kjusnkhldy">
                {item.to ? <Link to={item.to} onClick={() => setMobileOpen(false)} className="text-sm text-white/80" data-id="element-1419" data-mp="2n0xieanswgwd">
                    {item.label}
                  </Link> : <a href="#accueil" className="text-sm text-white/80" data-id="element-1420" data-mp="28a6ql2m7lht9">
                    {item.label}
                  </a>}
              </li>)}
          </ul>
        </nav>}
    </header>;
}